// client/src/components/pages/LexiStar/Maintenance/maintenanceLogic.ts (ST-00249)
//
// What the Maintenance page, its dialogs, the control room's truck actions and Plan & Dispatch's
// "in the workshop" warning share, pure: the four time categories (label, colour, icon), SAST
// formatting, an event's words, the timeline geometry, the page's summary counts, and the plan
// warning (an open or booked maintenance / breakdown event over a trip's planned start).
//
// The time model itself is the server's (GET /api/lexistar/time-model, timeModel.ts): the page
// draws its segments and never re-derives them, so the timeline and every report agree.
// Precedence there: Breakdown > Planned maintenance > Delay > Available.

import type { ComponentType } from 'react';
import { FaCarCrash, FaCheckCircle, FaHourglassHalf, FaWrench } from 'react-icons/fa';
import { MINING_TZ } from '../../../../utils/dateUtils';
import {
  ASSET_STATUS_CLASSES,
  MAINTENANCE_CATEGORY_KEY,
  type AssetStatusClasses,
} from '../../../map/assetStatus';
import type {
  EventCategory,
  TimeCategory,
  TimeEvent,
  TimeSegment,
} from '../../../../types/lexistar-maintenance';

// ── Categories ──────────────────────────────────────────────────────────────────────────

export interface CategoryMeta {
  label: string;
  /** One word for tight places (legend on a phone, chips). */
  short: string;
  /** What it means, in a line (legend, glossary-style). */
  hint: string;
  Icon: ComponentType<{ className?: string; size?: number; 'aria-hidden'?: boolean | 'true' }>;
  /** The timeline bar and the legend swatch (the key's solid). */
  bar: string;
  /** A pill / badge: tint background, strong text, ring. */
  pill: string;
  /** The accent text (counts, durations). */
  text: string;
  /** The panel tint of a summary tile. */
  panel: string;
}

/**
 * The colours of a category: the shared asset status palette (map/assetStatus, ST-00253 /
 * ST-00244), so Breakdown, Planned maintenance and Delay here are the same tokens as the control
 * room's sub-groups and Delayed group, the Status Board pills and the Live View / Replay markers.
 * The tokens flip with the theme (CSS custom properties). Delay's solid is orange-600 (3:1 on
 * white; orange-500 held only 2.8:1).
 */
function keyColours(c: EventCategory): Pick<CategoryMeta, 'bar' | 'pill' | 'text' | 'panel'> {
  const key = MAINTENANCE_CATEGORY_KEY[c];
  if (!key) throw new Error(`maintenanceLogic: no status key for ${c}`);
  const { bar, pill, text, panel }: AssetStatusClasses = ASSET_STATUS_CLASSES[key];
  return { bar, pill, text, panel };
}

export const CATEGORY_META: Record<TimeCategory, CategoryMeta> = {
  breakdown: {
    label: 'Breakdown',
    short: 'Breakdown',
    hint: 'Broken down: not available until it is repaired',
    Icon: FaCarCrash,
    ...keyColours('breakdown'),
  },
  planned_maintenance: {
    label: 'Planned maintenance',
    short: 'Planned',
    hint: 'Booked into the workshop: a service, tyres, an inspection',
    Icon: FaWrench,
    ...keyColours('planned_maintenance'),
  },
  delay: {
    label: 'Delay',
    short: 'Delay',
    hint: 'Able to work but held up: a queue, no driver, the weather',
    Icon: FaHourglassHalf,
    ...keyColours('delay'),
  },
  // Available has no status key: the empty track.
  available: {
    label: 'Available',
    short: 'Available',
    hint: 'No maintenance, breakdown or delay booked',
    Icon: FaCheckCircle,
    bar: 'bg-slate-200',
    pill: 'bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200',
    text: 'text-slate-700',
    panel: 'bg-surface border-slate-200',
  },
};

/** Legend and summary order: the precedence, strongest first, then Available. */
export const CATEGORY_ORDER: TimeCategory[] = ['breakdown', 'planned_maintenance', 'delay', 'available'];
export const EVENT_CATEGORY_ORDER: EventCategory[] = ['breakdown', 'planned_maintenance', 'delay'];

/** The three ways in: Book maintenance, Report a breakdown, Log a delay. */
export type BookingMode = 'maintenance' | 'breakdown' | 'delay';

export const MODE_CATEGORY: Record<BookingMode, EventCategory> = {
  maintenance: 'planned_maintenance',
  breakdown: 'breakdown',
  delay: 'delay',
};

export const MODE_TEXT: Record<BookingMode, { title: string; submit: string; codeLabel: string; done: string }> = {
  maintenance: { title: 'Book maintenance', submit: 'Book maintenance', codeLabel: 'What work', done: 'booked' },
  breakdown: { title: 'Report a breakdown', submit: 'Report breakdown', codeLabel: 'What happened', done: 'reported broken down' },
  delay: { title: 'Log a delay', submit: 'Log delay', codeLabel: 'Why', done: 'logged as delayed' },
};

/** The striped look of a booked (projected) stretch. */
export const PROJECTED_STYLE = {
  backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.55) 0 4px, transparent 4px 8px)',
  opacity: 0.8,
} as const;

// ── Time (SAST) ─────────────────────────────────────────────────────────────────────────

const timeFmt = new Intl.DateTimeFormat('en-GB', { timeZone: MINING_TZ, hour: '2-digit', minute: '2-digit', hour12: false });
const dayFmt = new Intl.DateTimeFormat('en-GB', { timeZone: MINING_TZ, weekday: 'short', day: 'numeric', month: 'short' });
const ymdFmt = new Intl.DateTimeFormat('en-CA', { timeZone: MINING_TZ, year: 'numeric', month: '2-digit', day: '2-digit' });

const msOf = (v: string | number | Date | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const t = v instanceof Date ? v.getTime() : typeof v === 'number' ? v : Date.parse(v);
  return Number.isFinite(t) ? t : null;
};

/** The SAST calendar day of an instant, YYYY-MM-DD. */
export const sastDay = (ms: number): string => ymdFmt.format(ms);

/** "14:00" (same SAST day as `ref`), else "Tue 20 Oct 14:00". */
export function fmtWhen(v: string | number | Date | null | undefined, ref: number = Date.now()): string {
  const t = msOf(v);
  if (t === null) return '—';
  return sastDay(t) === sastDay(ref) ? timeFmt.format(t) : `${dayFmt.format(t)} ${timeFmt.format(t)}`;
}

/** "Tue 20 Oct 14:00", always with the day. */
export const fmtDayTime = (v: string | number | Date | null | undefined): string => {
  const t = msOf(v);
  return t === null ? '—' : `${dayFmt.format(t)} ${timeFmt.format(t)}`;
};

/** "45m", "3h 20m", "2d 4h". */
export function fmtDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '—';
  const min = Math.max(0, Math.round(ms / 60000));
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 48) return min % 60 ? `${h}h ${String(min % 60).padStart(2, '0')}m` : `${h}h`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

/** Hours with one decimal ("12.5 h"), for the time-model figures. */
export const fmtHours = (seconds: number): string => {
  const h = seconds / 3600;
  return `${h >= 100 ? Math.round(h) : Math.round(h * 10) / 10} h`;
};

// ── An event in words ───────────────────────────────────────────────────────────────────

export const STATE_TEXT: Record<TimeEvent['state'], string> = {
  booked: 'Booked',
  open: 'Open now',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

/** "since 06:10 · expected out 14:00", "08:00 – 12:00", "booked Tue 20 Oct 08:00 – 12:00". */
export function eventWhen(e: TimeEvent, now: number = Date.now()): string {
  const start = fmtWhen(e.starts_at, now);
  if (e.state === 'closed' || e.state === 'cancelled') {
    return `${start} – ${e.ends_at ? fmtWhen(e.ends_at, msOf(e.starts_at) ?? now) : e.expected_end_at ? fmtWhen(e.expected_end_at, msOf(e.starts_at) ?? now) : '…'}`;
  }
  if (e.state === 'booked') {
    return `${start}${e.expected_end_at ? ` – ${fmtWhen(e.expected_end_at, msOf(e.starts_at) ?? now)}` : ''}`;
  }
  const until = e.expected_end_at
    ? e.overdue
      ? `expected ${fmtWhen(e.expected_end_at, now)}, still open`
      : `expected until ${fmtWhen(e.expected_end_at, now)}`
    : 'no end time yet';
  return `since ${start} · ${until}`;
}

/** How long it took (closed), has taken so far (open) or is booked for. */
export function eventDuration(e: TimeEvent, now: number = Date.now()): number | null {
  const s = msOf(e.starts_at);
  if (s === null) return null;
  const end = e.ends_at ? msOf(e.ends_at) : e.state === 'booked' ? msOf(e.expected_end_at) : now;
  return end === null ? null : Math.max(0, end - s);
}

// ── Plan & Dispatch: "in the workshop" ──────────────────────────────────────────────────

export interface WorkshopWarning {
  text: string;
  event: TimeEvent;
}

/**
 * The interval a maintenance / breakdown event keeps a truck away for planning: a closed one its
 * real [start, end); an open or booked one to its expected end while that is ahead, else
 * open-ended (it is not over until someone closes it) — the server's overlap rule.
 */
function awayInterval(e: TimeEvent, now: number): { start: number; end: number } | null {
  if (e.state === 'cancelled') return null;
  const start = msOf(e.starts_at);
  if (start === null) return null;
  const ends = msOf(e.ends_at);
  if (ends !== null) return { start, end: ends };
  const expected = msOf(e.expected_end_at);
  return { start, end: expected !== null && expected > now ? expected : Infinity };
}

/**
 * Plan & Dispatch's warning (never a block) when the truck has a planned-maintenance or breakdown
 * event over the planned start: "In the workshop: Brake service until 14:00", "Broken down:
 * Engine failure since 06:10, no end time yet". A breakdown wins over planned maintenance.
 */
export function workshopWarning(
  events: readonly TimeEvent[],
  assetId: string | null | undefined,
  at: string | number | Date | null | undefined,
  now: number = Date.now()
): WorkshopWarning | null {
  const t = msOf(at);
  if (!assetId || t === null) return null;
  const hits = events
    .filter((e) => e.asset_id === assetId && (e.category === 'breakdown' || e.category === 'planned_maintenance'))
    .map((e) => ({ e, iv: awayInterval(e, now) }))
    .filter((x): x is { e: TimeEvent; iv: { start: number; end: number } } => !!x.iv && x.iv.start <= t && t < x.iv.end)
    .sort(
      (a, b) =>
        (a.e.category === 'breakdown' ? 0 : 1) - (b.e.category === 'breakdown' ? 0 : 1) || a.iv.start - b.iv.start
    );
  const hit = hits[0];
  if (!hit) return null;
  const e = hit.e;
  const ref = t;
  const end = e.ends_at
    ? `until ${fmtWhen(e.ends_at, ref)}`
    : e.expected_end_at && msOf(e.expected_end_at)! > now
      ? `until ${fmtWhen(e.expected_end_at, ref)}`
      : e.state === 'booked'
        ? `from ${fmtWhen(e.starts_at, ref)}`
        : `since ${fmtWhen(e.starts_at, ref)}, no end time yet`;
  const text =
    e.category === 'breakdown' ? `Broken down: ${e.label} ${end}` : `In the workshop: ${e.label} ${end}`;
  return { text, event: e };
}

// ── The timeline ────────────────────────────────────────────────────────────────────────

export interface Bar {
  /** % from the left of the window. */
  left: number;
  /** % of the window. */
  width: number;
  segment: TimeSegment;
}

/** The segments of one truck as bars over [from, to) (Available left out: it is the track). */
export function barsOf(segments: readonly TimeSegment[], fromMs: number, toMs: number): Bar[] {
  const span = toMs - fromMs;
  if (!(span > 0)) return [];
  return segments
    .filter((s) => s.category !== 'available')
    .map((s) => {
      const a = Math.max(fromMs, Date.parse(s.start));
      const b = Math.min(toMs, Date.parse(s.end));
      return { left: ((a - fromMs) / span) * 100, width: ((b - a) / span) * 100, segment: s };
    })
    .filter((b) => b.width > 0);
}

export interface Tick {
  left: number;
  label: string;
  /** A day boundary (a stronger line). */
  major: boolean;
}

/**
 * One tick per SAST day (labelled "Mon 19", or "19" with `short`), with 06/12/18 h ticks for
 * windows up to 3 days. `maxLabels`: at most that many day labels (the others are lines only).
 */
export function ticksOf(fromMs: number, toMs: number, { short = false, maxLabels = Infinity }: { short?: boolean; maxLabels?: number } = {}): Tick[] {
  const span = toMs - fromMs;
  if (!(span > 0)) return [];
  const DAY = 86400000;
  const out: Tick[] = [];
  const firstDay = Date.parse(`${sastDay(fromMs)}T00:00:00+02:00`);
  const dayLabel = new Intl.DateTimeFormat('en-GB', short ? { timeZone: MINING_TZ, day: 'numeric' } : { timeZone: MINING_TZ, weekday: 'short', day: 'numeric' });
  const hours = span <= 3 * DAY && !short;
  const step = span > 20 * DAY ? 7 : 1;
  const days = Math.ceil(span / DAY);
  const labelStep = Math.max(1, Math.ceil(days / step / maxLabels));
  for (let d = firstDay, i = 0; d < toMs; d += DAY, i++) {
    if (d >= fromMs && i % step === 0)
      out.push({ left: ((d - fromMs) / span) * 100, label: (i / step) % labelStep === 0 ? dayLabel.format(d) : '', major: true });
    if (hours) {
      for (const h of [6, 12, 18]) {
        const t = d + h * 3600000;
        if (t > fromMs && t < toMs) out.push({ left: ((t - fromMs) / span) * 100, label: `${String(h).padStart(2, '0')}:00`, major: false });
      }
    }
  }
  return out;
}

/** Where "now" falls in the window (%), or null outside it. */
export const nowLeft = (fromMs: number, toMs: number, now: number): number | null =>
  now >= fromMs && now < toMs ? ((now - fromMs) / (toMs - fromMs)) * 100 : null;

// ── The page's summary ──────────────────────────────────────────────────────────────────

export interface MaintenanceSummary {
  breakdownNow: number;
  workshopNow: number;
  delayedNow: number;
  bookedAhead: number;
  overdue: number;
}

/** Open now per category (trucks, not events), and bookings starting in the next 7 days. */
export function summaryOf(events: readonly TimeEvent[], now: number = Date.now()): MaintenanceSummary {
  const trucks = (cat: EventCategory) =>
    new Set(events.filter((e) => e.state === 'open' && e.category === cat).map((e) => e.asset_id)).size;
  return {
    breakdownNow: trucks('breakdown'),
    workshopNow: trucks('planned_maintenance'),
    delayedNow: trucks('delay'),
    bookedAhead: events.filter((e) => e.state === 'booked' && (msOf(e.starts_at) ?? 0) - now <= 7 * 86400000).length,
    overdue: events.filter((e) => e.overdue).length,
  };
}

/** Share of a total, as a whole percentage ("92%"), or "—". */
export const pctText = (part: number, total: number): string =>
  total > 0 ? `${Math.round((part / total) * 100)}%` : '—';

/** A window from the page's two days (SAST): [from 00:00, to + 1 day 00:00) as instants. */
export function windowOf(from: string, to: string): { fromMs: number; toMs: number } {
  const fromMs = Date.parse(`${from}T00:00:00+02:00`);
  const toMs = Date.parse(`${to}T00:00:00+02:00`) + 86400000;
  return { fromMs, toMs };
}

/** YYYY-MM-DD `n` days from `day` (SAST calendar). */
export function addDays(day: string, n: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + n * 86400000).toISOString().slice(0, 10);
}
